import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { SEO, seoConfig } from "@/hooks/useSEO";
import { generateWebPageSchema, generateBreadcrumbSchema, generateArticleSchema } from "@/lib/jsonld";

// Images
import coverImage from "@/assets/blog/outils-fidelisation-client/couverture.webp";
import les7OutilsImage from "@/assets/blog/outils-fidelisation-client/les-7-outils.webp";
import parcoursNotifImage from "@/assets/blog/outils-fidelisation-client/parcours-notification.webp";
import matriceImage from "@/assets/blog/outils-fidelisation-client/matrice-priorisation.webp";
import statistiquesImage from "@/assets/blog/outils-fidelisation-client/statistiques-fidelisation.webp";

const OutilsFidelisationClient = () => {
  const jsonLdSchemas = [
    generateWebPageSchema(
      "Les meilleurs outils de fidélisation client : le guide complet 2026",
      seoConfig.blogOutilsFidelisation.description,
      "/meilleurs-outils-de-fidelisation-client"
    ),
    generateBreadcrumbSchema([
      { name: "Accueil", path: "/" },
      { name: "Blog", path: "/blog" },
      { name: "Outils de fidélisation client", path: "/meilleurs-outils-de-fidelisation-client" }
    ]),
    generateArticleSchema(
      "Les meilleurs outils de fidélisation client : le guide complet 2026",
      seoConfig.blogOutilsFidelisation.description,
      "https://unotif.com/meilleurs-outils-de-fidelisation-client",
      "2026-03-13"
    )
  ];

  const imageStyle = {
    boxShadow: "0 8px 32px -8px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08)",
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO {...seoConfig.blogOutilsFidelisation} jsonLd={jsonLdSchemas} />
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="pt-24 lg:pt-40 pb-12 sm:pb-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumb */}
            <nav className="text-sm font-medium text-[#495456] mb-6">
              <Link to="/blog" className="text-[#0c97a9] hover:underline">Blog</Link>
              <span className="mx-2">›</span>
              <span className="text-[#074155]">Outils de fidélisation client</span>
            </nav>

            {/* H1 */}
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-8">
              Les meilleurs outils de fidélisation client : le guide complet 2026
            </h1>

            {/* Cover Image */}
            <img
              src={coverImage}
              alt="Les meilleurs outils de fidélisation client"
              loading="eager"
              className="rounded-2xl w-full h-auto"
              style={imageStyle}
            />
          </div>
        </section>

        {/* Article Content */}
        <section className="pb-20 sm:pb-28 lg:pb-36">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* Introduction */}
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Acquérir un nouveau client coûte <strong className="font-semibold">5 à 7 fois plus cher</strong> que d'en fidéliser un existant. C'est une donnée établie par Bain & Company, régulièrement confirmée par les études terrain. Et pourtant, la majorité des commerçants, coachs et entrepreneurs concentrent 80 % de leur énergie sur l'acquisition, et presque rien sur la rétention.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le résultat est toujours le même : un panier percé. On attire des clients, ils achètent une ou deux fois, puis disparaissent. Sans stratégie de fidélisation, chaque nouveau client est une dépense, pas un investissement.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Ce guide présente les <strong className="font-semibold">7 outils de fidélisation client</strong> les plus efficaces en 2026, explique comment choisir le bon selon votre profil, et donne les bases pour construire une base client qui revient et qui reste.
            </p>

            {/* H2 - Pourquoi la fidélisation client est-elle devenue indispensable ? */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Pourquoi la fidélisation client est-elle devenue indispensable ?
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un client fidèle ne se contente pas de revenir. Il <strong className="font-semibold">dépense en moyenne 67 % de plus</strong> qu'un nouveau client (Bain & Company). Il recommande plus facilement, supporte mieux les hausses de prix, et coûte beaucoup moins cher à convaincre à chaque achat.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              À l'inverse, <strong className="font-semibold">un client perdu est rarement récupéré</strong>. Une étude de Harvard Business Review montre qu'une augmentation de seulement 5 % du taux de rétention peut accroître les profits de 25 à 95 %. Ces chiffres ne sont pas des théories : ils se vérifient dans les cafés, les boutiques, les cabinets de coaching et les formations en ligne.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              En 2026, la pression concurrentielle s'est intensifiée sur tous les canaux. Les publicités coûtent plus cher. Les algorithmes des réseaux sociaux réduisent la portée organique. <strong className="font-semibold">Les commerçants qui résistent sont ceux qui ont construit une base client qui n'a pas besoin d'être reconquise à chaque fois.</strong>
            </p>

            {/* H2 - Les 7 outils de fidélisation client à connaître */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Les 7 outils de fidélisation client à connaître
            </h2>

            {/* Image Les 7 outils */}
            <img
              src={les7OutilsImage}
              alt="Les 7 outils de fidélisation"
              loading="lazy"
              className="rounded-2xl w-full h-auto my-8"
              style={imageStyle}
            />

            {/* 1. CRM */}
            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              1. Le CRM : la mémoire de votre relation client
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un <strong className="font-semibold">CRM (Customer Relationship Management)</strong> centralise toutes les données de vos clients : coordonnées, historique d'achats, préférences, date du dernier contact. Il transforme une base de données froide en outil de personnalisation.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Concrètement, un CRM vous permet de savoir que Marie n'est pas venue depuis 45 jours, que Thomas a acheté trois fois le même type de produit, ou qu'un segment de clients fidèles n'a reçu aucune communication depuis deux mois. <strong className="font-semibold">Ces informations sont la base de toute action de rétention pertinente.</strong>
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le CRM est recommandé pour les structures avec une base client supérieure à 200 personnes. En dessous de ce seuil, un tableur bien tenu suffit souvent. Des options accessibles existent : HubSpot propose une version gratuite fonctionnelle, Zoho CRM est utilisable dès 14 euros par mois.
            </p>

            {/* 2. Programme de fidélité */}
            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              2. Le programme de fidélité : l'outil le plus sous-estimé quand il est bien configuré
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La <strong className="font-semibold">carte de fidélité</strong> existe depuis des décennies. Mais sa forme a évolué, et la différence entre une carte papier, une carte numérique et une <strong className="font-semibold">carte wallet</strong> est considérable en termes de résultats.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La carte papier se perd, s'oublie, ne génère aucune donnée. La carte numérique via application est plus efficace, mais elle demande un téléchargement que beaucoup de clients ne feront pas. <strong className="font-semibold">La carte wallet</strong>, intégrée directement dans Apple Wallet ou Google Wallet, s'installe en moins de 30 secondes via un QR code et reste visible en permanence dans le téléphone.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les données le confirment : <strong className="font-semibold">une carte de fidélité digitale augmente de 20 à 30 % la fréquence de visite</strong> des clients qui l'utilisent. C'est l'outil de fidélisation le plus universel, accessible à tous les profils et tous les budgets.
            </p>

            {/* 3. Email marketing */}
            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              3. L'email marketing : un canal qui reste pertinent avec les bons usages
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              L'<strong className="font-semibold">email marketing</strong> est souvent sous-utilisé ou mal utilisé. Envoyé intelligemment, avec un contenu utile et personnalisé, il reste un canal puissant pour maintenir le lien entre deux achats.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Son taux d'ouverture moyen oscille entre <strong className="font-semibold">23 et 30 %</strong> selon les secteurs. C'est moins que le SMS ou le wallet, mais l'email permet de transporter du contenu riche : une newsletter, un guide pratique, des actualités du commerce. Son coût est très faible, et les outils comme Mailchimp ou Brevo sont accessibles dès le premier euro.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Ses limites sont réelles : <strong className="font-semibold">la saturation des boîtes mail augmente chaque année</strong>, les filtres anti-spam deviennent plus stricts, et la délivrabilité se dégrade si la liste n'est pas entretenue. L'email fonctionne bien en complément d'un canal plus direct, pas en canal unique de fidélisation.
            </p>

            {/* 4. SMS marketing */}
            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              4. Le SMS marketing : le roi du taux d'ouverture
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un SMS a un <strong className="font-semibold">taux d'ouverture supérieur à 90 %</strong> et est lu en moyenne dans les 3 minutes suivant sa réception. C'est inégalé. Pour les offres flash, les rappels de rendez-vous ou les relances de clients inactifs, le SMS est redoutable.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Ses contraintes sont connues : <strong className="font-semibold">un coût par message</strong> (entre 0,05 et 0,10 euro selon les volumes), l'<strong className="font-semibold">opt-in obligatoire</strong> (le client doit avoir explicitement accepté de recevoir des SMS), et une limite naturelle sur la longueur du message. Le SMS irritant ou trop fréquent génère des désabonnements immédiats.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Il est recommandé de réserver le SMS à des messages à forte valeur temporelle, et de ne pas dépasser 2 à 3 envois par mois par client.
            </p>

            {/* 5. Programme de parrainage */}
            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              5. Le programme de parrainage : fidéliser en acquérant
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un <strong className="font-semibold">programme de parrainage</strong> remplit deux objectifs simultanément : il récompense un client existant pour sa recommandation, et il attire un nouveau client avec une offre d'entrée.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              C'est particulièrement efficace pour les services et abonnements, où la recommandation d'un pair a plus de poids qu'une publicité. Un client qui a parrainé quelqu'un est aussi <strong className="font-semibold">plus engagé</strong> : il s'est publiquement associé à votre marque. Son taux de rétention sur les 12 mois suivants est systématiquement plus élevé que la moyenne.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La mécanique est simple : "Parrainez un ami, recevez 10 euros de bon d'achat, votre filleul reçoit 10 euros à son premier achat." Rien de plus compliqué n'est nécessaire pour démarrer.
            </p>

            {/* 6. Notifications push via wallet */}
            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              6. Les notifications push via le wallet mobile : le canal que la plupart des commerçants ignorent encore
            </h3>

            {/* Image Parcours Notification */}
            <img
              src={parcoursNotifImage}
              alt="Parcours Notification Push Wallet"
              loading="lazy"
              className="rounded-2xl w-full h-auto my-8"
              style={imageStyle}
            />

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              En 2026, <strong className="font-semibold">37,8 millions de cartes wallet sont actives en France</strong>. Apple Wallet et Google Wallet sont préinstallés sur tous les smartphones. Le wallet mobile est devenu un canal de communication à part entière, et la plupart des commerçants ne l'utilisent pas encore.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Voici ce que permet une carte wallet couplée aux notifications push : envoyer un message directement sur l'écran verrouillé du téléphone du client, <strong className="font-semibold">sans algorithme, sans spam folder, sans application à télécharger</strong>. Le client a ajouté votre carte une fois via un QR code ; vous pouvez le contacter à tout moment, et la carte se met à jour dynamiquement (solde de points, offre en cours, date d'expiration).
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les chiffres sont clairs : <strong className="font-semibold">taux d'ouverture des push wallet à 53 %</strong>, contre 23 à 30 % pour l'email. <strong className="font-semibold">47 % des Français déclarent être influencés par les notifications push wallet.</strong> Les clients walleted visitent en moyenne <strong className="font-semibold">37 % plus fréquemment</strong> les commerces qui utilisent ce canal.
            </p>

            {/* 7. Avis clients et NPS */}
            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              7. Les avis clients et le NPS : l'outil de fidélisation invisible
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Demander un avis à un client, c'est lui signifier qu'il compte. C'est aussi l'un des actes de fidélisation les plus sous-estimés.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le <strong className="font-semibold">Net Promoter Score (NPS)</strong> consiste à poser une seule question : "Sur une échelle de 0 à 10, quelle est la probabilité que vous nous recommandiez ?" Les clients qui répondent 9 ou 10 sont des <strong className="font-semibold">promoteurs</strong> actifs. Ceux qui répondent 7 ou 8 sont des <strong className="font-semibold">passifs</strong> satisfaits mais non engagés. En dessous, ce sont des <strong className="font-semibold">détracteurs</strong> qui peuvent nuire à votre réputation.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un NPS mesuré chaque trimestre vous donne un signal clair sur l'état de votre relation client. Et le simple fait de demander, puis de répondre aux retours, améliore la perception de votre marque.
            </p>

            {/* H2 - Comment choisir le bon outil */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Comment choisir le bon outil selon votre profil
            </h2>

            {/* Image Matrice */}
            <img
              src={matriceImage}
              alt="Matrice de priorisation des outils digitaux"
              loading="lazy"
              className="rounded-2xl w-full h-auto my-8"
              style={imageStyle}
            />

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Tous les outils de fidélisation ne sont pas adaptés à tous les profils. Voici une grille de lecture rapide :
            </p>

            {/* Tableau comparatif */}
            <div className="overflow-x-auto my-8">
              <table className="w-full text-left text-[14px] sm:text-base font-medium text-[#495456] border-collapse">
                <thead>
                  <tr className="border-b-2 border-[#0c97a9]/30">
                    <th className="py-3 pr-4 font-semibold text-[#022226]">Profil</th>
                    <th className="py-3 pr-4 font-semibold text-[#022226]">Priorité n°1</th>
                    <th className="py-3 pr-4 font-semibold text-[#022226]">Priorité n°2</th>
                    <th className="py-3 font-semibold text-[#022226]">Budget mensuel estimé</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 pr-4">Commerce local (café, boutique, salon)</td>
                    <td className="py-3 pr-4">Carte fidélité wallet</td>
                    <td className="py-3 pr-4">Notifications push</td>
                    <td className="py-3">30 à 80 €</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 pr-4">E-commerce</td>
                    <td className="py-3 pr-4">Email marketing</td>
                    <td className="py-3 pr-4">Programme parrainage</td>
                    <td className="py-3">50 à 200 €</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 pr-4">Coach / Formateur</td>
                    <td className="py-3 pr-4">Newsletter</td>
                    <td className="py-3 pr-4">CRM léger</td>
                    <td className="py-3">0 à 50 €</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 pr-4">Restaurant</td>
                    <td className="py-3 pr-4">Carte fidélité digitale</td>
                    <td className="py-3 pr-4">SMS marketing</td>
                    <td className="py-3">30 à 80 €</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 pr-4">PME B2B</td>
                    <td className="py-3 pr-4">CRM</td>
                    <td className="py-3 pr-4">NPS automatisé</td>
                    <td className="py-3">100 à 300 €</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La règle la plus importante : <strong className="font-semibold">choisissez un ou deux outils et maîtrisez-les vraiment</strong>, plutôt que d'en installer cinq que vous n'utilisez pas. L'efficacité d'un outil de fidélisation vient de son animation régulière, pas de sa sophistication.
            </p>

            {/* H2 - Les erreurs qui font échouer */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Les erreurs qui font échouer 58 % des programmes de fidélisation
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">58 % des membres d'un programme de fidélité ne l'utilisent plus après 6 mois.</strong> Ce chiffre n'est pas une fatalité. Il est la conséquence d'erreurs évitables.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La première erreur est de <strong className="font-semibold">fixer des seuils de récompense trop élevés</strong>. Si un client doit acheter 20 fois pour obtenir un café offert, il abandonnera après la troisième visite. La récompense doit être atteignable en 3 à 5 visites maximum.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La deuxième erreur est <strong className="font-semibold">l'absence de communication après l'inscription</strong>. 85 % des clients découvrent un programme de fidélité par un vendeur en caisse, puis n'en entendent plus jamais parler. Un programme sans animation est un programme mort.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La troisième est le <strong className="font-semibold">manque de personnalisation</strong>. Offrir exactement la même chose à tous les clients, qu'ils achètent une fois par an ou trois fois par semaine, ne crée aucun sentiment de reconnaissance. Les meilleurs clients méritent un traitement différencié.
            </p>

            {/* H2 - Par où commencer si vous avez un petit budget */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Par où commencer si vous avez un petit budget ?
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La fidélisation ne demande pas un budget important. Elle demande de la méthode.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">La règle des 80/20 s'applique parfaitement ici :</strong> un seul outil bien utilisé, animé régulièrement, produit de meilleurs résultats que cinq outils configurés à moitié et abandonnés au bout d'un mois.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La progression recommandée pour une TPE ou un indépendant :
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Commencez par <strong className="font-semibold">créer un canal de contact direct</strong> avec vos clients. La carte fidélité wallet est le point d'entrée le plus accessible : installation en moins de 30 secondes pour le client, aucun téléchargement requis, données collectées dès la première utilisation.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Ajoutez ensuite les <strong className="font-semibold">notifications push</strong> pour animer votre base : un message par mois suffit pour maintenir l'engagement. Offre du moment, rappel de points, événement exclusif.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Enfin, mettez en place un <strong className="font-semibold">outil de mesure simple</strong> pour suivre si vos efforts portent leurs fruits : fréquence de visite, taux de réachat, nombre de récompenses utilisées.
            </p>

            {/* H2 - Comment mesurer */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Comment mesurer si vos outils de fidélisation fonctionnent ?
            </h2>

            {/* Image Statistiques */}
            <img
              src={statistiquesImage}
              alt="Statistiques de fidélisation client"
              loading="lazy"
              className="rounded-2xl w-full h-auto my-8"
              style={imageStyle}
            />

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">Un outil de fidélisation sans indicateurs de suivi est un outil aveugle.</strong> Vous investissez du temps et de l'argent sans savoir ce qui fonctionne et ce qui ne fonctionne pas.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les quatre indicateurs essentiels à suivre :
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le <strong className="font-semibold">taux de rétention</strong> mesure le pourcentage de clients qui restent actifs d'une période à l'autre. Un taux supérieur à 80 % est excellent pour un commerce local.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le <strong className="font-semibold">taux de réachat</strong> indique la proportion de clients ayant effectué au moins deux achats. En dessous de 30 %, le programme de fidélisation manque d'effet.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La <strong className="font-semibold">Customer Lifetime Value (CLV)</strong> estime le revenu total généré par un client sur toute la durée de sa relation avec vous. C'est l'indicateur stratégique par excellence : s'il augmente, votre fidélisation fonctionne.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le <strong className="font-semibold">NPS</strong> (Net Promoter Score) vous dit combien de clients sont susceptibles de vous recommander. Il se mesure en une question, une fois par trimestre.
            </p>

            {/* H2 - Unotif */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Unotif : l'outil qui combine carte wallet, notifications push et analytics
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La majorité des outils de fidélisation résolvent un problème à la fois : soit le stockage des données (CRM), soit la communication (email, SMS), soit la récompense (programme de points). Très peu combinent les trois dans une interface simple, accessible sans compétences techniques.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">Unotif</strong> est une plateforme de Wallet Marketing conçue pour les commerces locaux, les coachs, les formateurs et les entrepreneurs qui veulent un canal direct avec leurs clients, sans dépendre des algorithmes ni des boîtes mail.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Concrètement, Unotif permet de :
            </p>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>Créer une <strong className="font-semibold">carte wallet personnalisée</strong> (couleurs, logo, informations) en moins d'une heure</li>
              <li>La distribuer via un <strong className="font-semibold">QR code</strong> à afficher en caisse, sur les réseaux sociaux ou en signature d'email</li>
              <li>Envoyer des <strong className="font-semibold">notifications push</strong> directement sur le téléphone des clients ayant ajouté la carte</li>
              <li>Suivre les <strong className="font-semibold">métriques de fidélisation</strong> en temps réel depuis un tableau de bord simple</li>
            </ul>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le tout sans que vos clients aient à télécharger une application. Leur wallet est déjà sur leur téléphone.
            </p>

            {/* H2 - Conclusion */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Conclusion
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La fidélisation client n'est pas réservée aux grandes enseignes avec des équipes marketing et des budgets à six chiffres. <strong className="font-semibold">Elle est accessible à toute structure qui choisit les bons outils et les anime régulièrement.</strong>
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les 7 outils présentés dans ce guide couvrent tous les profils et tous les budgets. La carte fidélité wallet et les notifications push pour les commerces de proximité. L'email et le parrainage pour le e-commerce. Le CRM et le NPS pour les structures B2B ou les profils avec une base client plus large.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              L'essentiel est de commencer. <strong className="font-semibold">Un programme simple, lancé cette semaine, vaut mieux qu'un programme parfait jamais déployé.</strong> En 90 jours, les premiers résultats sont mesurables : fréquence de visite en hausse, taux de réachat amélioré, clients qui se souviennent de vous entre deux achats.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Testez Unotif gratuitement et envoyez votre première notification push en moins d'une heure.
            </p>

          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default OutilsFidelisationClient;
